import React, { FunctionComponent, useState, useEffect } from "react";

import { useMutation, useLazyQuery } from "@apollo/client";
import { addUserToOrgMutation, orgQuery } from "src/lib/graphql/org.queries";
import { userIdFromEmail } from "src/lib/graphql/user.queries";

import OrgUserRow from "./OrgUserRow";
import LoadingIndicator from "../../general/LoadingIndicator";
import { User, Org, Query } from "src/lib/graphql/types";
import { trackEvent } from "src/lib/analytics";

interface IOrgUsersEditProps {
  org: Org;
  orgLoading: boolean;
  orgAdmin: boolean;
  user: User;
  adminFlag?: boolean;
}

const OrgUsersEdit: FunctionComponent<IOrgUsersEditProps> = ({
  org,
  orgLoading,
  orgAdmin,
  user,
  adminFlag,
}) => {
  const [
    addUser,
    { loading: addUserLoading, error: addUserError },
  ] = useMutation(addUserToOrgMutation, {
    refetchQueries: [{ query: orgQuery, variables: { id: org.id } }],
    onCompleted: () => {
      if (!addUserError) {
        setNewUserEmail("");
      }
    },
  });

  const [newUserEmail, setNewUserEmail] = useState("");
  const [error, setError] = useState("");

  const [
    getUserIdAndAdd,
    { error: getUserError, loading: getUserLoading },
  ] = useLazyQuery<Query>(userIdFromEmail, {
    onCompleted: (data) => {
      if (data && data.user) {
        addUser({ variables: { orgId: org.id, userId: data.user.id } });
        trackEvent(
          {
            category: "Growth",
            action: "New Org Member: Main",
            label: `${org.shortname} <- ${data.user.shortname}`,
          },
          user
        );
      }
    },
  });

  useEffect(() => {
    if (addUserError && addUserError.message) {
      setError(addUserError.message.replace("GraphQL error: ", ""));
      setTimeout(() => setError(""), 3000);
    }
    if (getUserError && getUserError.message) {
      setError(getUserError.message.replace("GraphQL error: ", ""));
      setTimeout(() => setError(""), 3000);
    }
  }, [addUserError, getUserError]);

  const addUserToOrg = async () => {
    if (!newUserEmail) {
      return;
    }
    getUserIdAndAdd({ variables: { email: newUserEmail } });
  };

  const permissionOptions = org.roles?.map((role) => ({
    label: role.name,
    value: role.id,
  }));

  return (
    <div className="account-section-box">
      <div className="org-users-edit">
        <LoadingIndicator
          loading={orgLoading || addUserLoading || getUserLoading}
        />
        <table className="org-users-table">
          <thead>
            <tr>
              <th className="user-avatar" />
              <th className="user-name">{adminFlag ? "EMAIL" : "NAME"}</th>
              <th className="user-title">JOB TITLE</th>
              <th className="org-user-permissions">PERMISSIONS</th>
              <th className="remove-user">{orgAdmin ? "REMOVE" : ""}</th>
            </tr>
          </thead>
          <tbody>
            {[...(org.members || [])]
              .sort((a, b) => a.fullName.localeCompare(b.fullName))
              .map((orgUser) => (
                <OrgUserRow
                  key={orgUser.id}
                  org={org}
                  orgUser={orgUser}
                  orgAdmin={orgAdmin}
                  permissionOptions={permissionOptions || []}
                  user={user}
                  adminFlag={adminFlag}
                />
              ))}
            <tr className="add-org-user-row">
              <td className="user-avatar" />
              <td colSpan={2} className="add-org-user">
                <span className="error-text">{error}</span>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    addUserToOrg();
                  }}
                >
                  <input
                    type="email"
                    className={"input" + (error ? " error" : "")}
                    value={newUserEmail}
                    onChange={(e) => {
                      setNewUserEmail(e.target.value);
                      setError("");
                    }}
                    placeholder={"New user's email"}
                  />
                  <button className="secondary-button">Add User</button>
                </form>
              </td>
              <td colSpan={3} className="row-spacer" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrgUsersEdit;
